import ListInfo from '@/parcours/flows/shared/listInfo.vue';
import { usePreventionParcours } from '@/parcours/composables/usePreventionParcours';
import TextArea from '@/molecules/TextArea.vue';
import defaultValidators from '@/helpers/default-validators';
import SpecialistSelector from '@/parcours/components/specialistSelector.vue';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import { calculateBmi } from '@/parcours/flows/shared/helper';
import { MODAL_CONTENT_HEALTH_MEN } from '@/parcours/flows/healthMan/modalContentHealthMan';
import { getPatientAge } from '@/helpers/patient';

const { update, getById } = usePreventionParcours('/cms/coordinator-patient-health-men-infos');

export const healthMenFlow = withGlobalFlow({
  id: 'healthMenFlow',

  basePath: '/healthMenFlow/flow',

  exitRoute: '/healthMenFlow',

  async onInit({ route }) {
    const id = route.params.reservationId;
    const data = await getById(id);
    return {
      ...data,
      ...(data?.imc === undefined && {
        imc: calculateBmi(data?.height, data?.weight),
      }),
    };
  },

  steps: {
    modifiableRiskFactorsListMen: {
      type: 'component',
      component: ListInfo,
      title: 'Facteurs modifiables',
      props: (data: any) => {
        const age = getPatientAge(data?.flowData?.patient);
        return {
          data,
          config: [
            { key: 'height', label: 'Taille', suffix: 'cm' },
            { key: 'weight', label: 'Poids', suffix: 'kg' },
            { key: 'imc', label: 'IMC' },
            { label: 'Âge', value: age != null ? Math.floor(age) : null, suffix: 'ans' },
            { key: 'balanced_diet', label: 'Alimentation variée', type: 'boolean' },
            { key: 'exercise', label: 'Activité physique régulière', type: 'boolean' },
            { key: 'frequent_alcohol', label: "Consommation régulière d'alcool", type: 'boolean' },
            { key: 'smoking', label: 'Tabagisme', type: 'boolean' },
          ],
          actions: [
            {
              label: 'Conseils hygiènodiététiques',
              onClick: async ({ modal }: any) => {
                await modal.openModal({
                  title: 'Conseils hygiènodiététiques',
                  content: MODAL_CONTENT_HEALTH_MEN.HYGIENE,
                });
              },
            },
            {
              label: 'Conseils activité physique',
              onClick: async ({ modal }: any) => {
                await modal.openModal({
                  title: 'Conseils activité physique',
                  content: MODAL_CONTENT_HEALTH_MEN.SPORT,
                });
              },
            },
          ],
        };
      },
    },
    noModifiableRiskFactorsListMen: {
      type: 'component',
      component: ListInfo,
      title: 'Facteurs non-modifiables',
      props: (data: any) => ({
        data,
        config: [
          {
            key: 'testicular_cancer',
            label: 'Antécédents personnels de cancer des testicules',
            type: 'tri-boolean',
            section: 'Testicules',
          },
          {
            key: 'cryptorchidism',
            label: "Antécédent de cryptorchidie pendant l'enfance",
            type: 'tri-boolean',
          },
          {
            key: 'prostate_cancer',
            label: 'Antécédents personnels ou familiaux du cancer de la prostate',
            type: 'tri-boolean',
            section: 'Prostate',
          },
          {
            key: 'pesticides_agriculture',
            label: 'Travail exposant à des pesticides utilisés en agriculture',
            type: 'boolean',
          },
          {
            key: 'colon_or_rectal_cancer',
            label: 'Antécédents personnels ou familiaux de cancer du côlon ou du rectum',
            type: 'tri-boolean',
            section: 'Colorectal',
          },
          {
            key: 'genetic_diseases',
            label:
              'Antécédents personnels ou familiaux de maladies génétiques - la polypose adénomateuse familiale ou le syndrome de Lynch',
            type: 'tri-boolean',
          },
          {
            key: 'inflammatory_diseases',
            label:
              "Antécédents personnels ou familiaux de maladies inflammatoires chroniques de l'intestin (maladie de Crohn et la rectocolite hémorragique)",
            type: 'tri-boolean',
          },
        ],
      }),
    },
    preventionAndSensibilisation: {
      type: 'component',
      component: ListInfo,
      title: 'Prévention et sensibilisation',
      props: (data: any) => ({
        data,
        config: [
          {
            label: "Retrouvez toutes les informations sur les cancers de l'homme :",
            value: '',
            key: 'description',
          },
        ],
        actions: [
          {
            label: 'Symptômes cancers de l’homme',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Symptômes cancers de l’homme',
                content: MODAL_CONTENT_HEALTH_MEN.SYMPTOMS,
              });
            },
          },
          {
            label: 'Modalités de dépistage',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Modalités de dépistage',
                content: MODAL_CONTENT_HEALTH_MEN.MODALITY,
              });
            },
          },
          {
            label: 'Diagnostic et traitements',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Diagnostic et traitements',
                content: MODAL_CONTENT_HEALTH_MEN.TREATMENT,
              });
            },
          },
          {
            label: 'Autopalpation des testicules',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Autopalpation des testicules',
                content: MODAL_CONTENT_HEALTH_MEN.SELF_PALPATION,
              });
            },
          },
        ],
      }),
    },
    recommandation: {
      id: 'other_specialists',
      type: 'component',
      title: 'Recommandations',
      description:
        "Je recommande au patient d'aller consulter le(s) professionnel(s) de santé suivant(s):",
      component: SpecialistSelector,
      props: {
        specialists: [
          { name: 'Médecin généraliste', value: 'Médecin généraliste' },
          { name: 'Urologue', value: 'Urologue' },
          { name: 'Hépato-gastroentérologue', value: 'Hépato-gastroentérologue' },
          { name: 'Pharmacien', value: 'Pharmacien' },
        ],
      },
    },
    lastComment: {
      type: 'form',
      title: 'Un dernier commentaire ?',
      description: 'Autre commentaire ou recommandation pour le patient',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'other_recommendations',
            type: 'textarea',
            placeholder: 'Votre commentaire ...',
            as: TextArea,
            rules: defaultValidators.textOpt,
          },
        ],
      },
    },
  },

  transitions: {
    modifiableRiskFactorsListMen: { next: 'noModifiableRiskFactorsListMen' },
    noModifiableRiskFactorsListMen: { next: 'preventionAndSensibilisation' },
    preventionAndSensibilisation: { next: 'recommandation' },
    recommandation: { next: 'lastComment' },
    lastComment: { submit: true },
  },

  async onSubmit(data) {
    await update(data?.id, data);
  },
});
